import { useEffect, useRef } from "react";

export default function CareerParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let width = 0;
    let height = 0;
    let frame;
    let previous = 0;
    let particles = [];
    const pointer = { x: -1000, y: -1000 };

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      particles = Array.from({ length: width < 768 ? 30 : 64 }, (_, index) => ({
        x: Math.random() * width, y: Math.random() * height,
        vx: (Math.random() - .5) * .35, vy: -.12 - Math.random() * .22,
        radius: index % 9 === 0 ? 18 + Math.random() * 35 : 1.5 + Math.random() * 2,
        ring: index % 9 === 0,
      }));
      draw(0);
    }

    function draw(step) {
      context.clearRect(0, 0, width, height);
      particles.forEach((particle, index) => {
        particle.x += particle.vx * step;
        particle.y += particle.vy * step;
        const dx = particle.x - pointer.x;
        const dy = particle.y - pointer.y;
        const distance = Math.hypot(dx, dy);
        if (distance > 0 && distance < 150) {
          particle.x += dx / distance * (1 - distance / 150) * 1.5 * step;
          particle.y += dy / distance * (1 - distance / 150) * 1.5 * step;
        }
        if (particle.x < -60) particle.x = width + 60;
        if (particle.x > width + 60) particle.x = -60;
        if (particle.y < -60) particle.y = height + 60;
        if (particle.y > height + 60) particle.y = -60;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        if (particle.ring) {
          context.strokeStyle = "rgba(15,118,110,.23)";
          context.lineWidth = 1;
          context.stroke();
        } else {
          context.fillStyle = index % 2 ? "rgba(37,99,235,.38)" : "rgba(15,118,110,.45)";
          context.fill();
          particles.slice(index + 1).forEach((other) => {
            if (other.ring) return;
            const separation = Math.hypot(particle.x - other.x, particle.y - other.y);
            if (separation > 110) return;
            context.beginPath();
            context.moveTo(particle.x, particle.y);
            context.lineTo(other.x, other.y);
            context.strokeStyle = `rgba(15,118,110,${.13 * (1 - separation / 110)})`;
            context.stroke();
          });
        }
      });
    }

    function tick(time) {
      const step = previous ? Math.min((time - previous) / 16.67, 2) : 1;
      previous = time;
      draw(step);
      frame = requestAnimationFrame(tick);
    }
    function updateAnimation() {
      cancelAnimationFrame(frame);
      previous = 0;
      if (!motion.matches && !document.hidden) frame = requestAnimationFrame(tick);
      else draw(0);
    }
    function move(event) {
      if (motion.matches || event.pointerType === "touch") return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
    }
    function resetPointer() { pointer.x = -1000; pointer.y = -1000; }

    resize();
    updateAnimation();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", move, { passive: true });
    document.documentElement.addEventListener("pointerleave", resetPointer);
    document.addEventListener("visibilitychange", updateAnimation);
    motion.addEventListener("change", updateAnimation);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", move);
      document.documentElement.removeEventListener("pointerleave", resetPointer);
      document.removeEventListener("visibilitychange", updateAnimation);
      motion.removeEventListener("change", updateAnimation);
    };
  }, []);

  return <canvas ref={canvasRef} className="career-page-particles" aria-hidden="true" />;
}
